<template>
  <nav class="crumbs hide-print" aria-label="Breadcrumb">
    <ol>
      <li v-for="(crumb, i) in visible" :key="i">
        <router-link v-if="crumb.to" :to="crumb.to">{{ crumb.label }}</router-link>
        <span v-else aria-current="page">{{ crumb.label }}</span>
      </li>
    </ol>
  </nav>
</template>

<script>
/**
 * The trail back up: Game → Boards → Board → Space.
 *
 * Every board and tile page uses this one component, so "back" means the same
 * thing everywhere instead of each page inventing its own link. Pass whatever
 * you know; empty crumbs are dropped, so a page still renders a useful trail
 * while it is loading or when a board has no game.
 */
export default {
  name: 'BoardCrumbs',
  props: {
    gameName: { type: String, default: '' },
    gameId: { type: String, default: '' },
    boardName: { type: String, default: '' },
    boardId: { type: String, default: '' },
    // The page you are on, shown as plain text at the end of the trail.
    current: { type: String, default: '' }
  },
  computed: {
    visible() {
      const crumbs = [];
      if (this.gameId) {
        crumbs.push({
          label: this.gameName || 'Game',
          to: `/board-game/${this.gameId}`
        });
        crumbs.push({ label: 'Boards', to: `/build-boards/${this.gameId}` });
      }
      // Only link the board when it is not the page you are already on.
      if (this.boardId && this.current) {
        crumbs.push({
          label: this.boardName || 'Board',
          to: `/boards/${this.boardId}`
        });
      }
      if (this.current) crumbs.push({ label: this.current });
      else if (this.boardName) crumbs.push({ label: this.boardName });
      return crumbs;
    }
  }
};
</script>

<style scoped>
.crumbs { margin-bottom: 1rem; }
.crumbs ol {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0.82rem;
}
.crumbs li { display: flex; align-items: center; gap: 0.35rem; min-width: 0; }
/* The separator is generated, so the markup stays a clean list. */
.crumbs li + li::before {
  content: "/";
  color: var(--ink-light, #b3aca0);
}
.crumbs a {
  color: var(--ink-light, #7a7a7a);
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 22ch;
}
.crumbs a:hover { color: var(--pop-orange, #ff6b35); text-decoration: underline; }
.crumbs span {
  color: var(--ink, #2b2b2b);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 22ch;
}

@media (max-width: 575.98px) {
  /* On a phone the immediate parent is what you actually need; the rest is
     noise that pushes the page content down. */
  .crumbs li:not(:nth-last-child(-n + 2)) { display: none; }
  .crumbs li:nth-last-child(2)::before { content: "←"; }
}
</style>
